import React from 'react'
import 'project/polyfill'
import { NextPageWithLayout } from '../types/nextPageWithLayout'
import SeoContainer, {
  SeoContainerProps,
} from 'components/util/containers/SeoContainer'
import { socialImpactStrings } from 'common/strings/social-impact-strings'
import Strings from 'project/localisation'
import CommunutyGoalsCard from 'components/cards/CommunityGoalsCard'
import VisionImages from 'components/company/VisionImages'
import { homeBreadcrumb } from 'components/util/containers/Breadcrumbs'
import FullScreenHero from 'components/hero/FullScreenHero'
import useWindowSize from 'components/util/useWindowSize'
import classNames from 'classnames'
import OtherCommunityPages from 'components/company/OtherCommunityPages'
import VisionColumns from 'components/company/VisionColumns'
import TalkToUs from 'components/company/TalkToUs'
import imageResize from 'project/image-proxy-web'

export type HomePageType = {}
const HomePage: NextPageWithLayout<HomePageType> = () => {
  const seoProps: SeoContainerProps['seoProps'] = {
    description: socialImpactStrings.seoDescription,
    openGraph: {
      url: 'https://www.hoxtonmix.com/social-impact/',
    },
    title: socialImpactStrings.seoTitle,
  }
  const breadcrumb = homeBreadcrumb().concat([
    {
      item: 'https://www.hoxtonmix.com/social-impact/',
      name: `${Strings.socialImpact}`,
      position: 2,
    },
  ])
  const size = useWindowSize()
  return (
    <SeoContainer breadcrumb={breadcrumb} seoProps={seoProps}>
      <FullScreenHero
        title={Strings.socialImpact}
        subtitle={`${socialImpactStrings.ourCommunityMission}`}
        backgroundImage={imageResize(
          require('public/images/hero/social-impact-desktop.jpg'),
        )}
      />
      <main className={'overflow-hidden'}>
        <CommunutyGoalsCard
          heroTitle={`${Strings.socialImpact}`}
          heroSubtitle={`${socialImpactStrings.ourCommunityMission}`}
          title={socialImpactStrings.theVision}
          subtitle={
            socialImpactStrings.weArePassionateAboutEntrepreneurshipAndHelpingBusinessesThrive
          }
        />
        <div className='bg-gradient-pink2 pt-xxl-52 pt-lg-21 pt-12'>
          <div className='container'>
            <VisionColumns
              category={socialImpactStrings.community}
              title={socialImpactStrings.communityEngagement}
              text={socialImpactStrings.communityEngagementContent}
            >
              <div className='col-lg-4 ms-auto me-0 position-relative'>
                <img
                  loading='lazy'
                  className='img-fluid position-lg-absolute end-0 bottom-n50p me-4'
                  alt={'Community'}
                  src={imageResize(
                    require('public/images/company/community-2.png'),
                  )}
                />
              </div>
            </VisionColumns>
          </div>
          <div className='container-lg px-lg-4 px-0'>
            <VisionImages
              images={[
                {
                  alt: 'Community',
                  className:
                    'img-fluid d-lg-none d-block mt-7 ms-auto me-0 z-index-1 position-relative',
                  src: require('public/images/company/community-2.png'),
                  width: '49.9%',
                },
                {
                  alt: 'Community',
                  className:
                    'img-fluid mt-lg-19 mt-n6 z-index-0 position-relative',
                  src: require('public/images/company/community-1.jpeg'),
                  width: size.isLgOrHigher ? '46.7%' : '76%',
                },
                {
                  alt: 'Planning',
                  className: 'img-fluid d-block ms-auto me-0 mt-lg-9 mt-n6',
                  src: require('public/images/company/planning.png'),
                  width: size.isLgOrHigher ? '68%' : '84.5%',
                },
                {
                  alt: 'Activities',
                  className: 'img-fluid mt-lg-n26 mt-n4',
                  src: require('public/images/company/activities.png'),
                  width: size.isLgOrHigher ? '54%' : '82.5%',
                },
              ]}
            />
          </div>
          <div className='container mt-xxl-52 mt-lg-57 mt-14'>
            <VisionColumns
              category={socialImpactStrings.localEconomy}
              title={socialImpactStrings.smeStat}
              text={socialImpactStrings.smeDescription}
            />
          </div>
          <div className='container-lg px-lg-4 px-0 position-relative pb-xxxl-62 pb-xxl-56 pb-xl-94 pb-lg-45 pb-46 mt-lg-0 mt-12'>
            <div
              className={
                'd-lg-flex justify-content-between align-items-center position-lg-absolute start-0 end-0 bottom-n70p'
              }
            >
              <img
                loading='lazy'
                className='ms-lg-22 d-lg-inline d-block'
                alt={'Exhibition'}
                style={{
                  height: 'auto',
                  width: size.isLgOrHigher ? '29.6%' : '322px',
                }}
                src={imageResize(
                  require('public/images/company/exhibition.png'),
                )}
              />
              <img
                loading='lazy'
                className={classNames('d-lg-inline d-block', {
                  'position-absolute end-0 bottom-n30p': !size.isLgOrHigher,
                })}
                alt={'Meet up'}
                style={{
                  height: 'auto',
                  width: size.isLgOrHigher ? '36%' : '292px',
                }}
                src={imageResize(
                  require('public/images/company/meet-up-2.png'),
                )}
              />
            </div>
          </div>
        </div>
        <div className={'bg-gradient-blue2 pt-xxxl-68 pt-xl-88 pt-lg-54 pt-60'}>
          <div className={'pb-lg-30'}>
            <div className='container'>
              <VisionColumns
                category={socialImpactStrings.betterTogether}
                title={socialImpactStrings.weAimToBringTogether}
                titleClasses={'pe-lg-8 pe-26'}
                text={socialImpactStrings.weAimToBringTogetherDescription}
              />
            </div>
            <div className='container-lg px-lg-4 px-0 mt-xxxl-56 mt-xl-38 mt-lg-12 d-flex flex-lg-row flex-column-reverse justify-content-lg-between'>
              <VisionImages
                images={[
                  {
                    alt: 'Work',
                    className: 'img-fluid d-block mt-lg-57 mt-n6',
                    src: require('public/images/company/work-1.png'),
                    width: size.isLgOrHigher ? '43.5%' : '323px',
                  },
                  {
                    alt: 'Work',
                    className:
                      'img-fluid d-block mt-lg-0 mt-7 mx-lg auto ms-auto me-0 z-index-1',
                    height: 'fit-content',
                    src: require('public/images/company/work-2.png'),
                    width: size.isLgOrHigher ? '43.5%' : '317px',
                  },
                ]}
              />
            </div>
            <div className='container mt-xxl-50 mt-xl-38 mt-lg-12 mt-14'>
              <VisionColumns
                category={socialImpactStrings.strengthIsOurDiversity}
                title={socialImpactStrings.strengthIsOurDiversityDescription}
                titleClasses={'pe-lg-38 pe-26'}
              />
            </div>
            <div className='container-lg px-lg-4 px-0 mt-xxxl-n38 mt-lg-n30 mt-8 position-relative z-index-1'>
              <VisionImages
                images={[
                  {
                    alt: 'Work',
                    className:
                      'img-fluid d-block position-lg-absolute start-0 bottom-n70p ms-lg-0 ms-n4',
                    src: require('public/images/company/workspace.png'),
                    width: size.isLgOrHigher ? '39%' : '152px',
                  },
                  {
                    alt: 'Work',
                    className:
                      'img-fluid d-block mt-lg-0 mt-n30 ms-auto me-lg-0 me-3',
                    src: require('public/images/company/workspace-2.png'),
                    width: size.isLgOrHigher ? '43%' : '171px',
                  },
                ]}
              />
            </div>
          </div>
          <img
            loading='lazy'
            src={imageResize(require('public/images/company/workspace-3.png'))}
            alt={'Workspace'}
            className={'img-fluid mt-lg-0 mt-n12 transform-scale-sm-1.5'}
          />
          <div className='pb-xxl-50 pb-lg-21 pb-14'>
            <div className='container-lg px-lg-4 px-0 position-relative mt-sm-n14 mt-8'>
              <VisionImages
                images={[
                  {
                    alt: 'Notebook',
                    className: classNames(
                      'img-fluid d-block position-absolute end-0 me-lg-8 me-3',
                      !size.isSmOrLower ? 'top-n21p' : 'top-n30p',
                    ),
                    src: require('public/images/company/notebook.png'),
                    width: !size.isSmOrLower ? '30%' : '118px',
                  },
                  {
                    alt: 'Inspire and infuse',
                    className: 'img-fluid d-block mx-auto',
                    height: 'fit-content',
                    src: require('public/images/company/inspire-and-infuse.png'),
                    width: !size.isSmOrLower ? '66%' : '257px',
                  },
                ]}
              />
            </div>
            <TalkToUs />
          </div>
        </div>
        <OtherCommunityPages
          items={[
            {
              slug: 'business-support',
              title: 'Business Support',
            },
            {
              slug: 'outreach',
              title: 'Outreach',
            },
          ]}
        />
        <div className={'spacer'} />
      </main>
    </SeoContainer>
  )
}

export default HomePage
